import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Clock, ChevronRight, Route as RouteIcon } from "lucide-react";
import { PageLoading, PageHeader } from "@/components/shared";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);
const STATUS_C = { READY: "bg-emerald-50 text-emerald-600", IN_PROGRESS: "bg-blue-50 text-blue-600", COMPLETED: "bg-slate-100 text-slate-500", LATE: "bg-amber-50 text-amber-700", EN_ROUTE: "bg-cyan/10 text-cyan-dark", ARRIVED: "bg-cyan/10 text-cyan-dark" };

const VisitCard = ({ v, i, onOpen }) => (
  <button onClick={onOpen} className="flex w-full items-center gap-3 border-b border-slate-100 p-4 text-left last:border-0 hover:bg-slate-50" data-testid={`wcare-visit-${v.id}`}>
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cyan/10 text-sm font-bold text-cyan-dark">{i}</div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2"><span className="truncate font-semibold text-navy">{v.patient_name}</span><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_C[v.status] || "bg-slate-100 text-slate-500"}`}>{v.status_label}</span></div>
      <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500"><Clock className="h-3.5 w-3.5" />{v.scheduled_start}–{v.scheduled_end} · {v.visit_type_label}</div>
      {v.address && <div className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-400"><MapPin className="h-3.5 w-3.5" />{v.address}</div>}
    </div>
    <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
  </button>
);

export default function WorkerCareTour() {
  const [date, setDate] = useState(today());
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { setD(null); api.get(`/care/worker/my-tours?date=${date}`).then((r) => setD(r.data)).catch(() => setD({ tours: [], unrouted_visits: [] })); }, [date]);
  const open = (id) => nav(`/app/worker/care/visits/${id}`);

  return (
    <div data-testid="worker-care-tour">
      <PageHeader icon={RouteIcon} title="Meine Tour" action={
        <div className="flex items-center gap-2">
          <button onClick={() => nav("/app/worker/care/day")} className="rounded-full bg-navy px-3 py-2 text-xs font-semibold text-white hover:bg-navy/90" data-testid="wcare-goto-day">Arbeitszeit</button>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="wcare-date" />
        </div>
      } />
      {!d ? <PageLoading /> : (
        <div className="space-y-5">
          {d.tours.map((tr) => (
            <div key={tr.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`wcare-tour-${tr.id}`}>
              <div className="flex items-center justify-between bg-navy px-4 py-3 text-white">
                <div><div className="font-heading font-extrabold">{tr.name}</div><div className="text-xs text-white/70">{tr.planned_start_time}–{tr.planned_end_time} · {tr.visits.length} Besuche</div></div>
                <span className="font-mono text-[10px] text-white/60">{tr.tour_number}</span>
              </div>
              {tr.visits.map((v, i) => <VisitCard key={v.id} v={v} i={i + 1} onOpen={() => open(v.id)} />)}
              {tr.visits.length === 0 && <div className="p-4 text-sm text-slate-400">Keine Besuche.</div>}
            </div>
          ))}
          {d.unrouted_visits?.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="wcare-unrouted">
              <div className="bg-slate-50 px-4 py-3 font-heading font-bold text-navy">Weitere Besuche</div>
              {d.unrouted_visits.map((v, i) => <VisitCard key={v.id} v={v} i={i + 1} onOpen={() => open(v.id)} />)}
            </div>
          )}
          {d.tours.length === 0 && (!d.unrouted_visits || d.unrouted_visits.length === 0) && (
            <div className="rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-400" data-testid="wcare-empty">Keine geplanten Besuche für diesen Tag.</div>
          )}
        </div>
      )}
    </div>
  );
}
